import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Col,
  Content,
  Dropdown,
  FlexboxGrid,
  Form,
  Grid,
  Radio,
  RadioGroup,
  Row,
  Schema,
  Stack,
  Text,
} from 'rsuite';
import PortalHeader from '../../components/layout/PortalHeader';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetApplyState,
  updatePermitApplicationData,
  clearAllDataExceptProperty,
} from '../../store/slices/apply/applySlice';
import {
  selectApplyApplicationData,
  selectConfigExpressApplyParcel,
  selectConfigExpressParcelVerbiage,
  selectConfigExpressRegistrationTypeVerbiage,
  selectResolvedRentalStructureOptions,
  selectConfigExpressApplyRentalStructureRequired,
  selectConfigExpressApplyUnitNumberRequired,
  selectConfigExpressVerbiage,
  selectConfigExpressRegistrationMultiplePermitsPerAPNPolicy,
  selectConfigExpressApplyUnitNumberDescription,
} from '../../store/slices/config/configSelectors';
import { CityRestrictedAddressLookup } from '../../components/form/AddressLookup';
import FormTextField from '../../components/form/FormTextField';
import Notice from '../../components/user-feedback/Notice';
import SelectedProperty from '../../components/apply/SelectedProperty';
import { lookupAPNAddress } from '../../api/apiAccess';
import {
  constructMarkup,
  isNullUndefinedOrEmptyObject,
} from '../../common/utils';
import {
  FLOWS_ENUM,
  MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM,
  RENTAL_STRUCTURE_STATUS_ENUM,
  UNIT_NUMBER_STATUS_ENUM,
} from '../../common/enums';
import { getLabelByKey } from '../../common/rentalStructureResolver';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import ApplyWarning from '../../components/apply/ApplyWarning';
import { getExistingLicensesForAPN } from '../../store/slices/apply/applyThunks';
import GenericApplyWarning from '../../components/apply/GenericApplyWarning';
import { useDebounce } from 'src/hooks/useDebounce';

const ApplyProperty = () => {
  // Dependencies and state
  const dispatch = useDispatch();
  const form = useRef();
  const [formError, setFormError] = useState({});
  const [formValues, setFormValues] = useState({});
  const [pageError, setPageError] = useState(false);
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState({});
  const [apn, setApn] = useState('');
  const debouncedApn = useDebounce(apn);
  const [showAddressLookup, setShowAddressLookup] = useState(false);
  const [showKnownField, setShowKnownField] = useState(false);
  const [selectedDropdownTitle, setSelectedDropdownTitle] =
    useState('Select one...');
  const [parcelNotFound, setParcelNotFound] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAPNInUseWarning, setIsAPNInUseWarning] = useState(false);
  const [hasGenericAPNLookupError, setHasGenericAPNLookupError] =
    useState(false);
  const [genericAPNErrorMessage, setGenericAPNErrorMessage] = useState('');
  const [existingLicenseData, setExistingLicenseData] = useState([]);

  // Selectors
  const selectedParcelConfig = useSelector(selectConfigExpressApplyParcel);
  const selectedVerbiage = useSelector(selectConfigExpressVerbiage);
  const selectedPermitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const selectedParcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);
  const selectedUnitNumberRequired = useSelector(
    selectConfigExpressApplyUnitNumberRequired
  );
  const selectedUnitNumberDescription = useSelector(
    selectConfigExpressApplyUnitNumberDescription
  );
  const selectedRentalStructureRequired = useSelector(
    selectConfigExpressApplyRentalStructureRequired
  );
  const selectedRentalStructureOptions = useSelector(
    selectResolvedRentalStructureOptions
  );
  const { property } = useSelector(selectApplyApplicationData);
  const multipleLicenseStatus = useSelector(
    selectConfigExpressRegistrationMultiplePermitsPerAPNPolicy
  );

  const [selectedRadioOption, setSelectedRadioOption] = useState(
    property ? 'lookup' : ''
  );
  const [parcelLookupLoading, setParcelLookupLoading] = useState(false);
  const [lookupInstructions, setLookupInstructions] = useState('');

  const hasData = useMemo(() => property !== undefined, [property]);

  useEffect(() => {
    if (hasData) {
      setSelectedRadioOption('lookup');
      setIsOptionSelected(true);
      setShowAddressLookup(true);
      if (
        selectedRentalStructureRequired !== RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN
      ) {
        setSelectedDropdownTitle(
          getLabelByKey(
            property.rentalstructure,
            selectedRentalStructureOptions
          ) || 'Select one...'
        );
      }
      setSelectedProperty({
        address: property.propertyaddress,
        apn: [property.propertyid],
      });
      setApn([property.propertyid]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, selectedRentalStructureRequired]);

  const formModel = useMemo(() => {
    return {
      propertyaddress: property?.propertyaddress ?? '',
      propertyid: property?.propertyid ?? '',
      unitnumber: property?.unitnumber ?? '',
      ...(selectedRentalStructureRequired !==
        RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
        rentalstructure: property?.rentalstructure ?? '',
      }),
    };
  }, [property, selectedRentalStructureRequired]);

  // Validation
  const { StringType } = Schema.Types;
  const validationModel = useMemo(() => {
    return {
      propertyaddress: StringType(),
      propertyid: StringType(),
      unitnumber:
        selectedUnitNumberRequired === UNIT_NUMBER_STATUS_ENUM.REQUIRED
          ? StringType().isRequired('A unit number is required')
          : StringType(),
      ...(selectedRentalStructureRequired ===
        RENTAL_STRUCTURE_STATUS_ENUM.REQUIRED && {
        rentalstructure: StringType().isRequired(
          'Select a rental structure to continue'
        ),
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRentalStructureRequired]);

  // On mount
  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  useEffect(() => {
    // Function dependencies
    const onAPNLookup = async () => {
      setParcelLookupLoading(true);
      setParcelNotFound(false);
      setSelectedProperty({});
      try {
        const result = await lookupAPNAddress(debouncedApn);
        const address = result?.address;
        if (result && address) {
          setParcelNotFound(false);
          const propertyDetails = {
            address: address,
            apn: [debouncedApn],
          };
          setFormValues((prevFormValues) => ({
            ...prevFormValues,
            propertyaddress: address,
          }));
          setSelectedProperty(propertyDetails);
        } else {
          setParcelNotFound(true);
          setSelectedProperty({});
        }
      } catch (error) {
        setParcelNotFound(true);
        setSelectedProperty({});
      } finally {
        setParcelLookupLoading(false);
      }
    };

    if (
      debouncedApn &&
      (!selectedProperty?.address ||
        selectedProperty?.address !== formValues['propertyaddress']) &&
      !(selectedProperty?.apn ?? []).includes(debouncedApn)
    ) {
      onAPNLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedApn]);

  useEffect(() => {
    if (selectedParcelConfig?.lookup_html?.length > 0) {
      setLookupInstructions(selectedParcelConfig.lookup_html);
    } else if (selectedParcelConfig?.lookup?.length > 0) {
      let lookupMarkup = `<a target="_blank" rel="noreferrer" href="${selectedParcelConfig.lookup}">Lookup on county website</a>`;
      setLookupInstructions(lookupMarkup);
    }
  }, [selectedParcelConfig]);

  const hideOrShowFieldSelection = (v) => {
    setIsOptionSelected(true);
    setPageError(false);
    form.current?.reset();
    formModel.propertyaddress = '';
    formModel.propertyid = '';
    formModel.unitnumber = '';
    if (
      selectedRentalStructureRequired !== RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN
    ) {
      formModel.rentalstructure = '';
    }
    setFormValues(formModel);
    setShowAddressLookup(v === 'lookup');
    setShowKnownField(v === 'known');
    setSelectedRadioOption(v);
    setSelectedProperty({});
  };

  const hideNextButton = useMemo(() => {
    return (
      ((multipleLicenseStatus ===
        MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM.BLOCK ||
        multipleLicenseStatus ===
          MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM.BLOCK_AND_SHOW_LICENSE_DETAILS) &&
        isAPNInUseWarning) ||
      hasGenericAPNLookupError
    );
  }, [multipleLicenseStatus, isAPNInUseWarning, hasGenericAPNLookupError]);

  const showWarning = useMemo(() => {
    return isAPNInUseWarning || hasGenericAPNLookupError;
  }, [isAPNInUseWarning, hasGenericAPNLookupError]);

  const dispatchProperty = async () => {
    if (
      (multipleLicenseStatus ===
        MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM.BLOCK ||
        multipleLicenseStatus ===
          MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM.BLOCK_AND_SHOW_LICENSE_DETAILS) &&
      isAPNInUseWarning
    ) {
      return false;
    } else {
      // Reset errors;
      setPageError(false);

      // Prevent navigating to next page until parcel lookup is finished
      if (parcelLookupLoading) return false;

      // Ensure all required page options have been selected
      if (!isOptionSelected) {
        setPageError(true);
        setParcelNotFound(false);
        setErrorMessage('One of the above options must be selected.');
        return false;
      }

      if (isNullUndefinedOrEmptyObject(selectedProperty)) {
        setErrorMessage('A property must be selected to continue');
        setParcelNotFound(false);
        setPageError(true);
        return false;
      }

      if (
        selectedRentalStructureRequired ===
          RENTAL_STRUCTURE_STATUS_ENUM.REQUIRED &&
        selectedDropdownTitle === 'Select one...'
      ) {
        setErrorMessage('A rental structure must be selected to continue');
        setParcelNotFound(false);
        setPageError(true);
        return false;
      }
      let isResidential = false;

      // If it's not currently showing the warning and config multi license status is not allow, perform the exisiting license check.
      if (
        !isAPNInUseWarning &&
        !hasGenericAPNLookupError &&
        multipleLicenseStatus !==
          MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM.ALLOW
      ) {
        // Check the input values
        if (!form?.current?.check()) return false;

        const existingLicenseInfo = await dispatch(
          getExistingLicensesForAPN(apn, formValues?.unitnumber)
        );
        if (existingLicenseInfo.hasError) {
          setHasGenericAPNLookupError(true);
          setGenericAPNErrorMessage(existingLicenseInfo.errorMessage);
          return false;
        }
        if (existingLicenseInfo.inUse) {
          setIsAPNInUseWarning(true);
          setExistingLicenseData(existingLicenseInfo.details || []);
          return false;
        }

        isResidential = existingLicenseInfo.isResidential;
      }

      let _formValues = { ...formValues };
      _formValues['propertyid'] = apn;
      _formValues['isResidential'] = isResidential;

      const currentProperty = property;
      const newProperty = { ..._formValues };
      const propertyChanged =
        !currentProperty ||
        currentProperty.propertyid !== newProperty.propertyid ||
        currentProperty.unitnumber !== newProperty.unitnumber;

      if (propertyChanged) {
        dispatch(clearAllDataExceptProperty());
      }

      // Continue with application creation
      setIsAPNInUseWarning(false);
      setHasGenericAPNLookupError(false);
      setGenericAPNErrorMessage('');
      dispatch(updatePermitApplicationData({ property: { ..._formValues } }));

      return true;
    }
  };

  const handleBackClick = () => {
    if (isAPNInUseWarning) {
      setIsAPNInUseWarning(false);
      return false;
    } else if (hasGenericAPNLookupError) {
      setHasGenericAPNLookupError(false);
      setGenericAPNErrorMessage('');
      return false;
    } else {
      dispatch(resetApplyState());
      return true;
    }
  };

  const onAddressSelected = (propertyInfo) => {
    if (propertyInfo) {
      const { address, apn } = propertyInfo;
      setApn(apn.length > 0 ? apn[0] : '');
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        propertyaddress: address,
      }));
      setSelectedProperty(propertyInfo);
    } else {
      setSelectedProperty({});
    }
  };

  const onPropertyIdChange = (value) => {
    if (value?.length > 3) {
      setApn(value);
    }
  };

  const onRentalStructureSelected = (option) => {
    setSelectedDropdownTitle(option.label);
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      rentalstructure: option.key,
    }));
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title={
              isAPNInUseWarning
                ? `This ${selectedParcelVerbiage} already has existing ${selectedPermitTypeVerbiage?.toLowerCase()}s and/or applications.`
                : `Property`
            }
            onNext={dispatchProperty}
            onBack={handleBackClick}
            hideNextButton={hideNextButton}
          >
            {showWarning && (
              <>
                {isAPNInUseWarning && (
                  <ApplyWarning details={existingLicenseData} />
                )}
                {!isAPNInUseWarning && (
                  <GenericApplyWarning message={genericAPNErrorMessage} />
                )}
              </>
            )}
            {!showWarning && (
              <Grid
                className="mt-12 ml-0"
                fluid
              >
                <Row>
                  <Col xs={24}>
                    <Text align="left">
                      We need to know the {selectedParcelConfig?.verbiage} of
                      the property you wish to make available for{' '}
                      {selectedVerbiage}. You can type in your{' '}
                      {selectedParcelConfig?.verbiage} directly below if you
                      already know it, or you can find the{' '}
                      {selectedParcelConfig?.verbiage} by typing in the property
                      address.
                    </Text>
                    <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                      <legend className="sr-only">
                        Property identification method
                      </legend>
                      <RadioGroup
                        name="radio-option-group"
                        defaultValue={selectedRadioOption}
                        onChange={hideOrShowFieldSelection}
                      >
                        <Radio value="known">
                          I know my {selectedParcelConfig?.verbiage}
                        </Radio>
                        <Radio value="lookup">
                          I want to type in the property address
                        </Radio>
                      </RadioGroup>
                    </fieldset>
                    <Form
                      className="mt-12"
                      ref={form}
                      checkTrigger="blur"
                      formValue={formValues}
                      onCheck={setFormError}
                      onChange={setFormValues}
                      fluid
                    >
                      {showAddressLookup && (
                        <div data-cy="propertyaddress">
                          <CityRestrictedAddressLookup
                            name="propertyaddress"
                            label="Address of the rental property"
                            hasError={formError['propertyaddress']}
                            validationRule={validationModel.propertyaddress}
                            value={formValues['propertyaddress']}
                            onItemSelected={onAddressSelected}
                          />
                        </div>
                      )}
                      {showKnownField && (
                        <>
                          <FormTextField
                            name="propertyid"
                            label={selectedParcelConfig?.verbiage}
                            hasError={formError['propertyid']}
                            validationRule={validationModel.propertyid}
                            value={formValues['propertyid']}
                            subtitle={() =>
                              lookupInstructions.length > 0 && (
                                <Text className="mb-8">
                                  {constructMarkup(lookupInstructions)}
                                </Text>
                              )
                            }
                            onChange={onPropertyIdChange}
                            bottomMarginOverrideClass="override-form-field-bottom-margin-4"
                          />
                          {parcelLookupLoading && <IconIndicator />}
                          {parcelNotFound && (
                            <Notice
                              className="mt-16"
                              message={`No address found for ${selectedParcelConfig?.verbiage}: ${apn}`}
                            />
                          )}
                        </>
                      )}

                      {Object.keys(selectedProperty).length > 0 && (
                        <>
                          <SelectedProperty
                            className="mv-16 "
                            propertyDetails={selectedProperty}
                          />
                          <FormTextField
                            name="unitnumber"
                            label={`Unit Number ${selectedUnitNumberRequired === UNIT_NUMBER_STATUS_ENUM.REQUIRED ? '(required if exists)' : '(optional)'}`}
                            subtitle={() =>
                              selectedUnitNumberDescription ? (
                                <Text className="mt-4 mb-8">
                                  {selectedUnitNumberDescription}
                                </Text>
                              ) : null
                            }
                            hasError={formError['unitnumber']}
                            errorMessage={formError['unitnumber']}
                            validationRule={validationModel.unitnumber}
                          />
                          {selectedRentalStructureRequired !==
                            RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && (
                            <Stack
                              className="mt-12"
                              direction="column"
                              alignItems="flex-start"
                            >
                              <Text as="h5">
                                Rental Structure{' '}
                                {selectedRentalStructureRequired ===
                                RENTAL_STRUCTURE_STATUS_ENUM.REQUIRED
                                  ? '(required)'
                                  : '(optional)'}
                              </Text>
                              <Dropdown
                                className="mt-4"
                                data-cy="rental-structure-dropdown"
                                title={selectedDropdownTitle}
                                menuStyle={{
                                  minWidth: '200px',
                                }}
                              >
                                {selectedRentalStructureOptions &&
                                  selectedRentalStructureOptions.map(
                                    (opt, optIdx) => (
                                      <Dropdown.Item
                                        key={`rental_structure_opt_${optIdx}`}
                                        active={
                                          opt.key === formValues.rentalstructure
                                        }
                                        onClick={() =>
                                          onRentalStructureSelected(opt)
                                        }
                                      >
                                        {opt.label}
                                      </Dropdown.Item>
                                    )
                                  )}
                              </Dropdown>
                            </Stack>
                          )}
                        </>
                      )}
                    </Form>
                    {pageError && (
                      <Notice
                        className="mt-16"
                        message={`${errorMessage}`}
                      />
                    )}
                  </Col>
                </Row>
              </Grid>
            )}
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ApplyProperty;
